import { type FC, type ReactNode, useCallback, useMemo, useRef } from 'react';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';

import { useKeyboardEvents } from '../../hooks/useKeyboardEvents';
import { type QuantitativeSize, type QuantitativeSizeString } from '../../types/sizes';
import { mergeSx } from '../../util/mui/sx';
import { quantitativeSizeToString } from '../../util/sizes';
import { Paper, type PaperProps } from '../structural/Paper';

import {
  type AnimatedPopoverTransformOrigin,
  PopoverPlacementsToTransformOrigins,
} from './constants';
import { SuspendedPopoverClickAwayContextObj } from './context';
import { Popper, type PopperProps } from './Popper';

const PopoverDimensionsPropertyNames = [
  'height',
  'maxHeight',
  'maxWidth',
  'minHeight',
  'minWidth',
  'width',
] as const;

type PopoverDimensionProperties<T = QuantitativeSize<'px'>> = Partial<
  Record<(typeof PopoverDimensionsPropertyNames)[number], T>
>;

const parsePopperDimensions = (
  props: PopoverDimensionProperties,
): PopoverDimensionProperties<QuantitativeSizeString<'px'>> =>
  [...PopoverDimensionsPropertyNames].reduce(
    (acc, key) => {
      if (props[key] === undefined) {
        return acc;
      }
      return { ...acc, [key]: quantitativeSizeToString(props[key], 'px') };
    },
    {} as PopoverDimensionProperties<QuantitativeSizeString<'px'>>,
  );

export interface PopoverProps extends Omit<PopperProps, 'children' | 'role'> {
  /**
   * The transform origin of the {@link Popover} content, applicable when the {@link Popover}'s
   * content is animating into an "open" state.
   *
   * @see {@link AnimatedPopoverTransformOrigin}
   */
  readonly animateTransformOrigin?: AnimatedPopoverTransformOrigin;
  /**
   * The content that is rendered inside of the {@link Popover} when it is open.
   */
  readonly children: ReactNode;
  /**
   * The z-index of the {@link Popover}'s content.
   *
   * @default 10
   */
  readonly contentZIndex?: number;
  readonly height?: QuantitativeSize<'px'>;
  readonly maxHeight?: QuantitativeSize<'px'>;
  readonly maxWidth?: QuantitativeSize<'px'>;
  readonly minHeight?: QuantitativeSize<'px'>;
  readonly minWidth?: QuantitativeSize<'px'>;
  /**
   * A callback that is invoked when the {@link Popover} closes due to a {@link KeyboardEvent}
   * associated with the "Escape" key.
   */
  readonly onClose: () => void;
  /**
   * Props that will be passed to the internal wrapper, {@link Paper}, component.
   */
  readonly paperProps?: Omit<PaperProps, 'children' | 'component' | 'maxWidth'>;
  /**
   * Whether or not the {@link Popover} should invoke the 'onClose' callback when the user presses
   * the "Escape" key.
   *
   * @default true
   */
  readonly shouldCloseOnEscape?: boolean;
  /**
   * @default false
   */
  readonly shouldRenderInPortal?: boolean;
  readonly width?: QuantitativeSize<'px'>;
}

interface UseSuspendedPopoverClickAwayProviderParams {
  readonly onClose: () => void;
}

const useSuspendedPopoverClickAwayProvider = ({
  onClose,
}: UseSuspendedPopoverClickAwayProviderParams) => {
  const suspensionCountRef = useRef(0);

  const suspend = useCallback((): (() => void) => {
    suspensionCountRef.current += 1;
    let didUnsuspend = false;
    return () => {
      if (didUnsuspend) {
        return;
      }
      didUnsuspend = true;
      suspensionCountRef.current = Math.max(suspensionCountRef.current - 1, 0);
    };
  }, []);

  const handleClickAway = useCallback((): void => {
    if (suspensionCountRef.current === 0) {
      onClose();
    }
  }, [onClose]);

  return useMemo(() => ({ handleClickAway, suspend }), [suspend, handleClickAway]);
};

export const Popover: FC<PopoverProps> = ({
  anchorEl,
  animateTransformOrigin,
  children,
  contentZIndex = 10,
  height,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  onClose,
  paperProps,
  placement = 'bottom',
  shouldCloseOnEscape = true,
  shouldRenderInPortal = false,
  width,
  ...props
}) => {
  useKeyboardEvents([{ action: onClose, isEnabled: shouldCloseOnEscape, key: 'Escape' }]);

  const { handleClickAway: handleSuspendedClickAway, suspend } =
    useSuspendedPopoverClickAwayProvider({ onClose });

  /**
   * Wraps the 'onClose' callback for the {@link ClickAwayListener} such that clicks originating
   * from the anchor element, the Popover's 'anchorEl', do not trigger a close.
   *
   * The anchor element is rendered outside of the {@link Popover}'s subtree, so the
   * {@link ClickAwayListener} would otherwise treat a click on it as a click "away" and invoke
   * 'onClose' on "mousedown".  When that anchor is the {@link Dropdown}'s toggle button, the
   * subsequent "click" event re-opens the {@link Popover}, producing a "close then re-open" flash.
   *
   * Ignoring anchor-originating clicks here leaves the anchor's own click handler as the sole
   * authority over the open/closed state.
   *
   * @param {MouseEvent | TouchEvent} event
   *   The DOM event forwarded by the {@link ClickAwayListener}.
   */
  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const anchor = typeof anchorEl === 'function' ? anchorEl() : anchorEl;
      const isAnchorClick =
        anchor instanceof Element && event.target instanceof Node && anchor.contains(event.target);
      if (isAnchorClick) {
        return;
      }
      handleSuspendedClickAway();
    },
    [handleSuspendedClickAway, anchorEl],
  );

  return (
    <Popper
      transition
      {...props}
      anchorEl={anchorEl}
      disablePortal={props.disablePortal ?? !shouldRenderInPortal}
      placement={placement}
      role={undefined}
      sx={mergeSx(props.sx, { zIndex: contentZIndex })}
    >
      {({ TransitionProps }) => (
        <ClickAwayListener
          mouseEvent='onMouseDown'
          onClickAway={handleClickAway}
          touchEvent='onTouchStart'
        >
          <Grow
            {...TransitionProps}
            style={{
              ...parsePopperDimensions({
                height,
                maxHeight,
                maxWidth,
                minHeight,
                minWidth,
                width,
              }),
              transformOrigin:
                animateTransformOrigin ?? PopoverPlacementsToTransformOrigins[placement],
            }}
          >
            <Paper
              {...paperProps}
              role='presentation'
              sx={mergeSx(paperProps?.sx, { overflow: 'hidden' })}
            >
              <SuspendedPopoverClickAwayContextObj value={{ suspend }}>
                {children}
              </SuspendedPopoverClickAwayContextObj>
            </Paper>
          </Grow>
        </ClickAwayListener>
      )}
    </Popper>
  );
};
